import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { TESTIMONIALS } from "@/data/content";

export default function TestimonialWall() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const count = TESTIMONIALS.length;

  const go = useCallback((next) => setIndex((i) => (i + next + count) % count), [count]);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setInterval(() => go(1), 6000);
    return () => clearInterval(timer.current);
  }, [paused, go]);

  const t = TESTIMONIALS[index];

  return (
    <section
      className="bg-ink text-paper py-20 lg:py-28 relative grain overflow-hidden"
      data-testid="testimonial-wall"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-8 relative z-10">
        <Reveal className="text-center mb-12">
          <Eyebrow className="mb-6 text-gold">Praise for Seeds of Change</Eyebrow>
        </Reveal>

        <div className="relative min-h-[260px] sm:min-h-[240px] flex flex-col items-center justify-center text-center" data-testid="testimonial-stage">
          <Quote className="w-8 h-8 text-gold mb-8 rotate-180" strokeWidth={1.2} aria-hidden="true" />
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              data-testid="testimonial-quote"
            >
              <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl leading-[1.25] text-balance">"{t.quote}"</p>
              <footer className="mt-8 font-mono text-xs uppercase tracking-[0.2em]">
                <span className="text-gold">— {t.source}</span>
                <span className="text-paper/50 block mt-2">{t.kind}</span>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-12 flex items-center justify-center gap-6">
          <button
            onClick={() => go(-1)}
            aria-label="Previous testimonial"
            className="w-11 h-11 rounded-full border border-paper/25 flex items-center justify-center text-paper/80 hover:bg-terra hover:border-terra hover:text-paper transition-colors duration-300"
            data-testid="testimonial-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2.5" role="tablist" aria-label="Choose testimonial">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={i === index}
                aria-label={`Testimonial ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-2 rounded-full transition-all duration-400 ${
                  i === index ? "w-8 bg-gold" : "w-2 bg-paper/30 hover:bg-paper/60"
                }`}
                data-testid={`testimonial-dot-${i + 1}`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            aria-label="Next testimonial"
            className="w-11 h-11 rounded-full border border-paper/25 flex items-center justify-center text-paper/80 hover:bg-terra hover:border-terra hover:text-paper transition-colors duration-300"
            data-testid="testimonial-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
}
